import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Title,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  ActionIcon,
  Menu,
  Text,
  Paper,
  Stack,
  Skeleton,
  Select,
  Pagination,
  PasswordInput,
} from '@mantine/core';
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
} from '@tabler/icons-react';
import { usersService } from '../../services/api/users';
import { useAuthStore } from '../../stores/auth';
import { useCreateMutation, useUpdateMutation, useFormModal, useTableSort, showValidationError } from '../../hooks';
import { useDisclosure } from '@mantine/hooks';
import { SearchToolbar, FormModal, SortableHeader, DetailModal, DetailField } from '../../components/shared';
import type { User, CreateUserInput, UpdateUserInput, UserRole } from '../../types';
import {
  USER_ROLE_COLORS,
  USER_ROLE_LABELS,
  USER_STATUS_COLORS,
  USER_STATUS_LABELS,
} from '../../constants/colors';
import dayjs from 'dayjs';

const INITIAL_CREATE_DATA: Partial<CreateUserInput> = { role: 'team_member' };

export function UsersPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [confirmPassword, setConfirmPassword] = useState('');

  const [viewModalOpened, { open: openViewModal, close: closeViewModal }] = useDisclosure(false);
  const [viewItem, setViewItem] = useState<User | null>(null);

  const { user: currentUser } = useAuthStore();
  const createModal = useFormModal<CreateUserInput>({ initialData: INITIAL_CREATE_DATA });
  const editModal = useFormModal<UpdateUserInput & { isActive?: boolean }, User>({ initialData: {} });
  const isEditingSelf = editModal.selectedItem?.id === currentUser?.id;

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.list(),
  });

  const createMutation = useCreateMutation(
    (data: CreateUserInput) => usersService.create(data),
    {
      queryKeys: ['users'],
      entityName: 'User',
      onSuccessCallback: createModal.close,
    }
  );

  const updateMutation = useUpdateMutation(
    ({ id, data }: { id: string; data: UpdateUserInput }) => usersService.update(id, data),
    {
      queryKeys: ['users'],
      entityName: 'User',
      onSuccessCallback: editModal.close,
    }
  );

  const handleCreateSubmit = () => {
    if (!createModal.formData.name || !createModal.formData.email || !createModal.formData.password) {
      showValidationError('Name, email, and password are required');
      return;
    }
    if (createModal.formData.password !== confirmPassword) {
      showValidationError('Passwords do not match');
      return;
    }
    createMutation.mutate(createModal.formData as CreateUserInput);
  };

  const handleEditSubmit = () => {
    if (!editModal.selectedItem) return;
    const { isActive, role, ...rest } = editModal.formData;
    const data: UpdateUserInput = {
      ...rest,
      ...(isEditingSelf ? {} : { role, status: isActive ? 'active' : 'inactive' }),
    };
    updateMutation.mutate({
      id: editModal.selectedItem.id,
      data,
    });
  };

  const openEdit = (user: User) => {
    editModal.openForEdit(user, {
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      isActive: user.status === 'active',
    });
  };

  const resetPage = useCallback(() => setPage(1), []);
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort<User>(resetPage);

  const users = usersData?.users || [];
  const filteredUsers = search
    ? users.filter(
        u =>
          (u.name ?? '').toLowerCase().includes(search.toLowerCase()) ||
          (u.email ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const sortAccessors: Record<string, (item: User) => unknown> = useMemo(() => ({
    name: (u) => u.name,
    email: (u) => u.email,
    phone: (u) => u.phone,
    role: (u) => u.role,
    status: (u) => u.status,
    created: (u) => u.createdAt,
  }), []);

  const sortedUsers = sortData(filteredUsers, sortAccessors);

  const PAGE_SIZE = 20;
  const totalPages = Math.ceil(sortedUsers.length / PAGE_SIZE);
  const paginatedUsers = sortedUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" mb="lg">
        <Title order={2}>Users</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setConfirmPassword(''); createModal.open(); }}>
          Create User
        </Button>
      </Group>

      <SearchToolbar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        placeholder="Search users..."
      />

      <Paper withBorder>
        {isLoading ? (
          <Stack p="md" gap="xs">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} height={50} />
            ))}
          </Stack>
        ) : filteredUsers.length === 0 ? (
          <Text p="xl" ta="center" c="dimmed">
            No users found
          </Text>
        ) : (
          <>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableHeader column="name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Name</SortableHeader>
                <SortableHeader column="email" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Email</SortableHeader>
                <SortableHeader column="phone" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Phone</SortableHeader>
                <SortableHeader column="role" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Role</SortableHeader>
                <SortableHeader column="status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Status</SortableHeader>
                <SortableHeader column="created" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Created</SortableHeader>
                <Table.Th w={50}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginatedUsers.map(u => (
                <Table.Tr key={u.id} onClick={() => { setViewItem(u); openViewModal(); }} style={{ cursor: 'pointer' }}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {u.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{u.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">{u.phone || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={USER_ROLE_COLORS[u.role]} variant="light">
                      {USER_ROLE_LABELS[u.role]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={USER_STATUS_COLORS[u.status]} variant="light">
                      {USER_STATUS_LABELS[u.status]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {dayjs(u.createdAt).format('MMM D, YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Menu position="bottom-end" withArrow>
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                        <Menu.Item
                          leftSection={<IconEdit size={14} />}
                          onClick={() => openEdit(u)}
                        >
                          Edit
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Text size="sm" c="dimmed">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, sortedUsers.length)}–{Math.min(page * PAGE_SIZE, sortedUsers.length)} of {sortedUsers.length}
            </Text>
            {totalPages > 1 && (
              <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
            )}
          </Group>
          </>
        )}
      </Paper>

      {/* View User Modal */}
      {viewItem && (
        <DetailModal
          opened={viewModalOpened}
          onClose={closeViewModal}
          title="User Details"
          recordId={viewItem.id}
          actions={[
            {
              label: 'Edit',
              icon: <IconEdit size={16} />,
              onClick: () => { closeViewModal(); openEdit(viewItem); },
            },
          ]}
        >
          <DetailField label="Name" value={viewItem.name} />
          <DetailField label="Email" value={viewItem.email} />
          <DetailField label="Phone" value={viewItem.phone} />
          <DetailField label="Role" value={
            <Badge color={USER_ROLE_COLORS[viewItem.role]} variant="light">{USER_ROLE_LABELS[viewItem.role]}</Badge>
          } />
          <DetailField label="Status" value={
            <Badge color={USER_STATUS_COLORS[viewItem.status]} variant="light">{USER_STATUS_LABELS[viewItem.status]}</Badge>
          } />
          <DetailField label="Last Login" value={viewItem.lastLoginAt ? dayjs(viewItem.lastLoginAt).format('MMM D, YYYY h:mm A') : 'Never'} />
          <DetailField label="Created" value={dayjs(viewItem.createdAt).format('MMM D, YYYY h:mm A')} />
          <DetailField label="Updated" value={dayjs(viewItem.updatedAt).format('MMM D, YYYY h:mm A')} />
        </DetailModal>
      )}

      {/* Create User Modal */}
      <FormModal
        opened={createModal.isOpen}
        onClose={createModal.close}
        title="Create User"
        isLoading={createMutation.isPending}
        onSubmit={handleCreateSubmit}
        submitLabel="Create"
      >
        <TextInput
          label="Name"
          placeholder="Full name"
          required
          value={createModal.formData.name || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, name: e.target.value })}
        />
        <TextInput
          label="Email"
          placeholder="user@example.com"
          required
          value={createModal.formData.email || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, email: e.target.value })}
        />
        <PasswordInput
          label="Password"
          placeholder="Minimum 8 characters"
          required
          value={createModal.formData.password || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, password: e.target.value })}
        />
        <PasswordInput
          label="Confirm Password"
          placeholder="Re-enter password"
          required
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          error={confirmPassword && createModal.formData.password !== confirmPassword ? 'Passwords do not match' : undefined}
        />
        <TextInput
          label="Phone"
          placeholder="Phone number"
          value={createModal.formData.phone || ''}
          onChange={e => createModal.setFormData({ ...createModal.formData, phone: e.target.value })}
        />
        <Select
          label="Role"
          data={[
            { value: 'team_member', label: 'Team Member' },
            { value: 'admin', label: 'Admin' },
          ]}
          value={createModal.formData.role || 'team_member'}
          onChange={v => createModal.setFormData({ ...createModal.formData, role: (v || 'team_member') as UserRole })}
        />
      </FormModal>

      {/* Edit User Modal */}
      <FormModal
        opened={editModal.isOpen}
        onClose={editModal.close}
        title="Edit User"
        isLoading={updateMutation.isPending}
        onSubmit={handleEditSubmit}
        submitLabel="Save Changes"
      >
        <TextInput
          label="Name"
          placeholder="Full name"
          required
          value={editModal.formData.name || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, name: e.target.value })}
        />
        <TextInput
          label="Phone"
          placeholder="Phone number"
          value={editModal.formData.phone || ''}
          onChange={e => editModal.setFormData({ ...editModal.formData, phone: e.target.value })}
        />
        <Select
          label="Role"
          data={[
            { value: 'team_member', label: 'Team Member' },
            { value: 'admin', label: 'Admin' },
          ]}
          value={editModal.formData.role || editModal.selectedItem?.role || 'team_member'}
          onChange={v => editModal.setFormData({ ...editModal.formData, role: (v || 'team_member') as UserRole })}
          disabled={isEditingSelf}
        />
        <Select
          label="Status"
          data={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
          ]}
          value={editModal.formData.isActive ? 'active' : 'inactive'}
          onChange={v => editModal.setFormData({ ...editModal.formData, isActive: v === 'active' })}
          disabled={isEditingSelf}
        />
      </FormModal>
    </Container>
  );
}
